import React, { useState, useRef, useEffect } from 'react';
import {
    Button, Card, CardBody, CardHeader,
    Col, Row, FormGroup, Label, Input,
    Modal, ModalHeader, ModalBody, ModalFooter,
    Badge
} from 'reactstrap';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';
import DdlProfile from '../../components/common/ddl-profile';
import Face1 from '../../assets/images/faces/1.png';
import { GetDataUserGroup } from '../../components/utils/data-storage';
// import '../../scss/components/_recurrent.scss';

// ============================================================
// MOCK DATA
// ============================================================
const MOCK_SCHEDULES = [
    {
        id: 1, title: 'รอบเช้า ทั่วไป', shift: 'morning',
        start_time: '06:00', end_time: '12:00', days: [1,2,3,4,5],
        date_start: '2025-01-01', date_end: '2025-06-30', status: '1'
    },
    {
        id: 2, title: 'รอบบ่าย ทั่วไป', shift: 'afternoon',
        start_time: '12:00', end_time: '18:00', days: [1,2,3,4,5],
        date_start: '2025-01-01', date_end: '2025-12-31', status: '1'
    },
    {
        id: 3, title: 'รอบเช้า วันหยุด', shift: 'morning',
        start_time: '07:00', end_time: '13:00', days: [6,0],
        date_start: '2025-03-01', date_end: null, status: '1'
    },
    {
        id: 4, title: 'รอบบ่าย วันหยุด', shift: 'afternoon',
        start_time: '13:00', end_time: '19:00', days: [6,0],
        date_start: '2025-03-01', date_end: '2025-09-30', status: '0'
    },
];

const DAY_LABELS = { 0:'อา', 1:'จ', 2:'อ', 3:'พ', 4:'พฤ', 5:'ศ', 6:'ส' };
const DAY_ORDER  = [0, 1, 2, 3, 4, 5, 6];
const DAY_SHORT  = { 0:'Su', 1:'Mo', 2:'Tu', 3:'We', 4:'Th', 5:'Fr', 6:'Sa' };

const SHIFT_OPTIONS = [
    { value: 'morning',   label: '🌅 รอบเช้า (Morning)' },
    { value: 'afternoon', label: '☀️ รอบบ่าย (Afternoon)' },
];

const MONTHS_TH = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.'];
const DAYS_TH   = ['อา','จ','อ','พ','พฤ','ศ','ส'];

function formatDateTH(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return `${d.getDate()} ${MONTHS_TH[d.getMonth()]} ${d.getFullYear() + 543}`;
}

// ============================================================
// DaySelector — circle toggle
// ============================================================
function DaySelector({ value = [], onChange, shift }) {
    const toggle = (day) => {
        if (value.includes(day)) onChange(value.filter(d => d !== day));
        else onChange([...value, day]);
    };
    return (
        <div className="day-selector">
            {DAY_ORDER.map(day => (
                <button
                    key={day}
                    type="button"
                    className={[
                        'day-selector__btn',
                        value.includes(day)
                            ? `day-selector__btn--active day-selector__btn--${shift || 'morning'}`
                            : '',
                    ].join(' ')}
                    onClick={() => toggle(day)}
                    title={DAY_LABELS[day]}
                >
                    {DAY_SHORT[day]}
                </button>
            ))}
        </div>
    );
}

// ============================================================
// TimePicker — grid picker (no input type="time")
// ============================================================
const HOURS   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '15', '30', '45'];

function TimePicker({ value, onChange, label }) {
    const [open, setOpen]       = useState(false);
    const [tab, setTab]         = useState('hour'); // 'hour' | 'minute'
    const wrapRef               = useRef();

    const hour   = value ? value.split(':')[0] : null;
    const minute = value ? value.split(':')[1] : null;

    const selectHour = (h) => {
        onChange(`${h}:${minute || '00'}`);
        setTab('minute');
    };

    const selectMinute = (m) => {
        onChange(`${hour || '00'}:${m}`);
        setOpen(false);
        setTab('hour');
    };

    // close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setTab('hour');
            }
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div className="tpk-wrapper" ref={wrapRef}>
            {/* Trigger */}
            <button
                type="button"
                className={`tpk-trigger ${open ? 'tpk-trigger--open' : ''} ${value ? '' : 'tpk-trigger--empty'}`}
                onClick={() => { setOpen(o => !o); setTab('hour'); }}
            >
                <i className="ri-time-line" />
                <span className="tpk-display">
                    {value
                        ? <><strong>{hour}</strong><span className="tpk-colon">:</span><strong>{minute}</strong></>
                        : <span className="tpk-placeholder">--:--</span>
                    }
                </span>
                <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line tpk-chevron`} />
            </button>

            {/* Popover */}
            {open && (
                <div className="tpk-popover">
                    {/* Tab bar */}
                    <div className="tpk-tabs">
                        <button
                            type="button"
                            className={`tpk-tab ${tab === 'hour' ? 'tpk-tab--active' : ''}`}
                            onClick={() => setTab('hour')}
                        >
                            <span className="tpk-tab-value">{hour ?? '--'}</span>
                            <span className="tpk-tab-label">ชั่วโมง</span>
                        </button>
                        <span className="tpk-tab-sep">:</span>
                        <button
                            type="button"
                            className={`tpk-tab ${tab === 'minute' ? 'tpk-tab--active' : ''}`}
                            onClick={() => setTab('minute')}
                        >
                            <span className="tpk-tab-value">{minute ?? '--'}</span>
                            <span className="tpk-tab-label">นาที</span>
                        </button>
                    </div>

                    {/* Hour grid */}
                    {tab === 'hour' && (
                        <div className="tpk-grid tpk-grid--hours">
                            {HOURS.map(h => (
                                <button
                                    key={h}
                                    type="button"
                                    className={`tpk-cell ${h === hour ? 'tpk-cell--selected' : ''}`}
                                    onClick={() => selectHour(h)}
                                >
                                    {h}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Minute grid */}
                    {tab === 'minute' && (
                        <div className="tpk-grid tpk-grid--minutes">
                            {MINUTES.map(m => (
                                <button
                                    key={m}
                                    type="button"
                                    className={`tpk-cell tpk-cell--min ${m === minute ? 'tpk-cell--selected' : ''}`}
                                    onClick={() => selectMinute(m)}
                                >
                                    :{m}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Footer quick-clear */}
                    {value && (
                        <div className="tpk-footer">
                            <button type="button" className="tpk-clear" onClick={() => { onChange(''); setOpen(false); }}>
                                <i className="ri-close-line" /> ล้างค่า
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ============================================================
// DateRangePicker Component
// ============================================================
function DateRangePicker({ valueStart, valueEnd, onChangeStart, onChangeEnd, noEndDate, onToggleNoEnd }) {
    const [open, setOpen]         = useState(false);
    const [selecting, setSelecting] = useState('start');
    const [hover, setHover]       = useState(null);

    const today = new Date();
    today.setHours(0,0,0,0);

    const parseDate = (str) => str ? new Date(str + 'T00:00:00') : null;
    const toStr = (d) => {
        if (!d) return null;
        const y  = d.getFullYear();
        const m  = String(d.getMonth()+1).padStart(2,'0');
        const dd = String(d.getDate()).padStart(2,'0');
        return `${y}-${m}-${dd}`;
    };

    const startDate = parseDate(valueStart);
    const endDate   = parseDate(valueEnd);

    const [leftYear,  setLeftYear]  = useState(today.getFullYear());
    const [leftMonth, setLeftMonth] = useState(today.getMonth());

    const rightMonth = leftMonth === 11 ? 0  : leftMonth + 1;
    const rightYear  = leftMonth === 11 ? leftYear + 1 : leftYear;

    const prevMonth = () => {
        if (leftMonth === 0) { setLeftMonth(11); setLeftYear(y => y-1); }
        else setLeftMonth(m => m-1);
    };
    const nextMonth = () => {
        if (leftMonth === 11) { setLeftMonth(0); setLeftYear(y => y+1); }
        else setLeftMonth(m => m+1);
    };

    const daysInMonth = (y, m) => new Date(y, m+1, 0).getDate();
    const firstDayOf  = (y, m) => new Date(y, m, 1).getDay();

    const handleDayClick = (dateObj) => {
        if (selecting === 'start') {
            onChangeStart(toStr(dateObj));
            onChangeEnd(null);
            setSelecting('end');
        } else {
            if (startDate && dateObj < startDate) {
                onChangeStart(toStr(dateObj));
                onChangeEnd(null);
                setSelecting('end');
            } else {
                onChangeEnd(toStr(dateObj));
                setSelecting('start');
                if (!noEndDate) setOpen(false);
            }
        }
    };

    const isInRange  = (d) => {
        if (!startDate) return false;
        const checkEnd = selecting === 'end' && hover ? hover : endDate;
        if (!checkEnd) return false;
        return d > startDate && d < checkEnd;
    };
    const isStart    = (d) => startDate && d.getTime() === startDate.getTime();
    const isEnd      = (d) => endDate && !noEndDate && d.getTime() === endDate.getTime();
    const isHoverEnd = (d) => selecting === 'end' && hover && d.getTime() === hover.getTime();

    const renderMonth = (year, month) => {
        const cells = [];
        const pad   = firstDayOf(year, month);
        const total = daysInMonth(year, month);
        for (let i = 0; i < pad; i++) cells.push(null);
        for (let d = 1; d <= total; d++) cells.push(new Date(year, month, d));

        return (
            <div className="drp-month">
                <div className="drp-month-header">{MONTHS_TH[month]} {year + 543}</div>
                <div className="drp-day-labels">{DAYS_TH.map(d => <span key={d}>{d}</span>)}</div>
                <div className="drp-grid">
                    {cells.map((date, i) => {
                        if (!date) return <span key={`e${i}`} className="drp-cell drp-cell--empty" />;
                        const inRange  = isInRange(date);
                        const sel      = isStart(date) || isEnd(date);
                        const hEnd     = isHoverEnd(date);
                        const isStartD = isStart(date);
                        const isEndD   = isEnd(date) || hEnd;
                        return (
                            <button
                                key={i}
                                className={[
                                    'drp-cell',
                                    sel     ? 'drp-cell--selected'    : '',
                                    inRange ? 'drp-cell--range'        : '',
                                    isStartD ? 'drp-cell--range-start' : '',
                                    isEndD   ? 'drp-cell--range-end'   : '',
                                    hEnd    ? 'drp-cell--hover-end'   : '',
                                ].join(' ')}
                                onClick={() => handleDayClick(date)}
                                onMouseEnter={() => selecting === 'end' && setHover(date)}
                                onMouseLeave={() => setHover(null)}
                            >
                                {date.getDate()}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    const popoverRef = useRef();
    useEffect(() => {
        const handler = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const displayStart = valueStart ? formatDateTH(valueStart) : 'วันที่เริ่มต้น';
    const displayEnd   = noEndDate ? 'ไม่มีวันสิ้นสุด' : (valueEnd ? formatDateTH(valueEnd) : 'วันที่สิ้นสุด');

    return (
        <div className="drp-wrapper" ref={popoverRef}>
            <div className="drp-trigger" onClick={() => setOpen(o => !o)}>
                <div className={`drp-trigger-segment ${selecting === 'start' && open ? 'drp-trigger-segment--active' : ''}`}
                     onClick={(e) => { e.stopPropagation(); setOpen(true); setSelecting('start'); }}>
                    <i className="ri-calendar-line" />
                    <span className={valueStart ? 'drp-trigger-value' : 'drp-trigger-placeholder'}>{displayStart}</span>
                </div>
                <span className="drp-trigger-arrow">→</span>
                <div className={`drp-trigger-segment ${noEndDate ? 'drp-trigger-segment--noend' : ''} ${selecting === 'end' && open ? 'drp-trigger-segment--active' : ''}`}
                     onClick={(e) => { e.stopPropagation(); setOpen(true); setSelecting('end'); }}>
                    <i className={noEndDate ? 'ri-infinity-line' : 'ri-calendar-line'} />
                    <span className={valueEnd || noEndDate ? 'drp-trigger-value' : 'drp-trigger-placeholder'}>{displayEnd}</span>
                </div>
            </div>

            {open && (
                <div className="drp-popover">
                    <div className="drp-popover-header">
                        <span className="drp-popover-hint">
                            {selecting === 'start' ? '📅 เลือกวันที่เริ่มต้น' : '📅 เลือกวันที่สิ้นสุด'}
                        </span>
                        <button className="drp-nav-btn" onClick={prevMonth}>‹</button>
                        <button className="drp-nav-btn" onClick={nextMonth}>›</button>
                    </div>
                    <div className="drp-months">
                        {renderMonth(leftYear, leftMonth)}
                        {renderMonth(rightYear, rightMonth)}
                    </div>
                    <div className="drp-footer">
                        <label className="drp-no-end-label">
                            <input
                                type="checkbox"
                                checked={noEndDate}
                                onChange={e => {
                                    onToggleNoEnd(e.target.checked);
                                    if (e.target.checked) { onChangeEnd(null); setOpen(false); }
                                }}
                            />
                            <i className="ri-infinity-line" /> ไม่มีวันสิ้นสุด
                        </label>
                        {(valueStart || valueEnd) && (
                            <button className="drp-clear-btn" onClick={() => {
                                onChangeStart(null); onChangeEnd(null);
                                onToggleNoEnd(false); setSelecting('start');
                            }}>
                                <i className="ri-close-line" /> ล้างค่า
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ============================================================
// Main Component
// ============================================================
const defaultForm = {
    title: '', shift: null,
    start_time: '', end_time: '',
    days: [], status: '1',
    date_start: null, date_end: null, no_end_date: false,
};

const Recurrent = () => {
    const [schedules,   setSchedules]   = useState(MOCK_SCHEDULES);
    const [modalOpen,   setModalOpen]   = useState(false);
    const [editItem,    setEditItem]    = useState(null);
    const [form,        setForm]        = useState(defaultForm);
    const [filterShift, setFilterShift] = useState(null);

    const GetUseRoleInPage = () => {
        if (GetDataUserGroup() === '2') return 'ADMIN';
        if (GetDataUserGroup() === '4') return 'REPORTER';
        return '';
    };

    const displayed = filterShift
        ? schedules.filter(s => s.shift === filterShift.value)
        : schedules;

    const OpenCreate = () => { setEditItem(null); setForm(defaultForm); setModalOpen(true); };

    const OpenEdit = (item) => {
        setEditItem(item);
        setForm({
            title:       item.title,
            shift:       SHIFT_OPTIONS.find(o => o.value === item.shift) || null,
            start_time:  item.start_time,
            end_time:    item.end_time,
            days:        item.days,
            status:      item.status,
            date_start:  item.date_start || null,
            date_end:    item.date_end   || null,
            no_end_date: !item.date_end,
        });
        setModalOpen(true);
    };

    const OnSave = () => {
        if (!form.title || !form.shift || !form.start_time || !form.end_time ||
            form.days.length === 0 || !form.date_start) {
            Swal.fire({ icon: 'warning', title: 'กรุณากรอกข้อมูลให้ครบ', confirmButtonText: 'ตกลง' });
            return;
        }
        const payload = {
            title:      form.title,
            shift:      form.shift.value,
            start_time: form.start_time,
            end_time:   form.end_time,
            days:       form.days,
            status:     form.status,
            date_start: form.date_start,
            date_end:   form.no_end_date ? null : form.date_end,
        };
        if (editItem) {
            setSchedules(prev => prev.map(s => s.id === editItem.id ? { ...s, ...payload } : s));
            Swal.fire({ icon: 'success', title: 'แก้ไขสำเร็จ', timer: 1500, showConfirmButton: false });
        } else {
            setSchedules(prev => [...prev, { id: Date.now(), ...payload }]);
            Swal.fire({ icon: 'success', title: 'สร้างสำเร็จ', timer: 1500, showConfirmButton: false });
        }
        setModalOpen(false);
    };

    const OnDelete = (id) => {
        Swal.fire({
            icon: 'warning', title: 'ยืนยันการลบ?', text: 'ไม่สามารถกู้คืนได้',
            showCancelButton: true, confirmButtonText: 'ลบ', cancelButtonText: 'ยกเลิก',
            confirmButtonColor: '#EF4444',
        }).then(result => {
            if (result.isConfirmed) {
                setSchedules(prev => prev.filter(s => s.id !== id));
                Swal.fire({ icon: 'success', title: 'ลบสำเร็จ', timer: 1200, showConfirmButton: false });
            }
        });
    };

    const OnToggleStatus = (id) => {
        setSchedules(prev =>
            prev.map(s => s.id === id ? { ...s, status: s.status === '1' ? '0' : '1' } : s)
        );
    };

    return (
        <React.Fragment>
            <Row>
                <Col xs={12} lg={9}>
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0">Recurrent Schedule</h4>
                            <Button color="primary" size="sm" onClick={OpenCreate}>
                                <i className="ri-add-line me-1" />สร้าง Schedule
                            </Button>
                        </CardHeader>
                        <CardBody>
                            {/* Filter */}
                            <Row className="mb-3">
                                <Col xs={12} md={4}>
                                    <FormGroup className="mb-0">
                                        <Label className="form-group-header">กรอง Shift</Label>
                                        <Select
                                            options={SHIFT_OPTIONS}
                                            value={filterShift}
                                            onChange={setFilterShift}
                                            isClearable
                                            placeholder="ทั้งหมด"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Cards */}
                            <div className="recurrent-list">
                                {displayed.length === 0 && (
                                    <div className="recurrent-empty">
                                        <i className="ri-calendar-line" />
                                        <p>ไม่มีข้อมูล Schedule</p>
                                    </div>
                                )}
                                {displayed.map(item => (
                                    <div key={item.id}
                                        className={`recurrent-card recurrent-card--${item.shift} ${item.status === '0' ? 'recurrent-card--inactive' : ''}`}
                                    >
                                        <div className="recurrent-card__bar" />
                                        <div className="recurrent-card__body">
                                            <div className="recurrent-card__top">
                                                <div>
                                                    <span className={`recurrent-badge recurrent-badge--${item.shift}`}>
                                                        {item.shift === 'morning' ? '🌅 เช้า' : '☀️ บ่าย'}
                                                    </span>
                                                    <h6 className="recurrent-card__title">{item.title}</h6>
                                                </div>
                                                <Badge
                                                    color={item.status === '1' ? 'success' : 'secondary'}
                                                    className="recurrent-status-badge"
                                                    onClick={() => OnToggleStatus(item.id)}
                                                    style={{ cursor: 'pointer' }}
                                                    title="คลิกเพื่อเปลี่ยนสถานะ"
                                                >
                                                    {item.status === '1' ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </div>
                                            <div className="recurrent-card__info">
                                                <span className="recurrent-card__time">
                                                    <i className="ri-time-line" />
                                                    {item.start_time} – {item.end_time}
                                                </span>
                                                <div className="recurrent-card__days">
                                                    {DAY_ORDER.map(d => (
                                                        <span key={d}
                                                            className={`day-chip ${item.days.includes(d) ? 'day-chip--active day-chip--' + item.shift : ''}`}
                                                        >
                                                            {DAY_SHORT[d]}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="recurrent-card__daterange">
                                                <i className="ri-calendar-event-line" />
                                                <span>{formatDateTH(item.date_start)}</span>
                                                <span className="recurrent-card__daterange-arrow">→</span>
                                                {item.date_end
                                                    ? <span>{formatDateTH(item.date_end)}</span>
                                                    : <span className="recurrent-card__daterange-noend"><i className="ri-infinity-line" /> ไม่มีวันสิ้นสุด</span>
                                                }
                                            </div>
                                        </div>
                                        <div className="recurrent-card__actions">
                                            <button className="btn-icon btn-icon--edit" onClick={() => OpenEdit(item)} title="แก้ไข">
                                                <i className="ri-edit-line" />
                                            </button>
                                            <button className="btn-icon btn-icon--delete" onClick={() => OnDelete(item.id)} title="ลบ">
                                                <i className="ri-delete-bin-line" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                {/* Profile */}
                <Col xs={12} lg={3}>
                    <Card>
                        <CardBody className="py-4 px-4">
                            <div className="d-flex align-items-center">
                                <div className="avatar avatar-xl">
                                    <img src={Face1} alt="Face 1" />
                                </div>
                                <div className="ms-3 name">
                                    <DdlProfile useRoleInPage={GetUseRoleInPage()} codeInPage="RCR" />
                                    <h6 className="text-muted mb-0">
                                        <Link to="/logout">Logout</Link>
                                    </h6>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ============ Modal ============ */}
            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="md" centered>
                <ModalHeader toggle={() => setModalOpen(false)}>
                    {editItem ? 'แก้ไข Schedule' : 'สร้าง Schedule ใหม่'}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>ชื่อ Schedule <span className="text-danger">*</span></Label>
                        <Input
                            value={form.title}
                            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                            placeholder="เช่น รอบเช้า วันธรรมดา"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>Shift <span className="text-danger">*</span></Label>
                        <Select
                            options={SHIFT_OPTIONS}
                            value={form.shift}
                            onChange={val => setForm(p => ({ ...p, shift: val }))}
                            placeholder="เลือก Shift"
                        />
                    </FormGroup>

                    {/* Time pickers — แทน input type="time" */}
                    <Row>
                        <Col xs={6}>
                            <FormGroup>
                                <Label>เวลาเริ่ม <span className="text-danger">*</span></Label>
                                <TimePicker
                                    value={form.start_time}
                                    onChange={val => setForm(p => ({ ...p, start_time: val }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Label>เวลาสิ้นสุด <span className="text-danger">*</span></Label>
                                <TimePicker
                                    value={form.end_time}
                                    onChange={val => setForm(p => ({ ...p, end_time: val }))}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup>
                        <Label>วันที่ทำซ้ำ <span className="text-danger">*</span></Label>
                        <DaySelector
                            value={form.days}
                            onChange={val => setForm(p => ({ ...p, days: val }))}
                            shift={form.shift?.value}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>
                            ช่วงวันที่ใช้งาน <span className="text-danger">*</span>
                            <small className="text-muted ms-1">(วันที่เริ่มต้น – วันที่สิ้นสุด)</small>
                        </Label>
                        <DateRangePicker
                            valueStart={form.date_start}
                            valueEnd={form.date_end}
                            noEndDate={form.no_end_date}
                            onChangeStart={val => setForm(p => ({ ...p, date_start: val }))}
                            onChangeEnd={val   => setForm(p => ({ ...p, date_end: val }))}
                            onToggleNoEnd={val => setForm(p => ({ ...p, no_end_date: val, date_end: val ? null : p.date_end }))}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>สถานะ</Label>
                        <div className="d-flex gap-3">
                            {[{v:'1',l:'Active'},{v:'0',l:'Inactive'}].map(opt => (
                                <div key={opt.v} className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        id={`status_${opt.v}`}
                                        checked={form.status === opt.v}
                                        onChange={() => setForm(p => ({ ...p, status: opt.v }))}
                                    />
                                    <label className="form-check-label" htmlFor={`status_${opt.v}`}>{opt.l}</label>
                                </div>
                            ))}
                        </div>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={OnSave}>บันทึก</Button>
                    <Button outline onClick={() => setModalOpen(false)}>ยกเลิก</Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default Recurrent;